import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../api/client.js';
import { DataTable, ErrorState, Field, Modal, Spinner, StatusBadge } from '../../components/ui.jsx';
import { useFetch, useTitle } from '../../lib/hooks.js';
import { useMeta } from '../../state/MetaContext.jsx';
import { useToast } from '../../state/ToastContext.jsx';
import { formatXaf, timeAgo } from '../../lib/format.js';

export const OFFER_BADGE = {
  submitted: { status: 'submitted', label: 'Waiting on the seller' },
  accepted: { status: 'approved', label: 'Accepted' },
  declined: { status: 'rejected', label: 'Declined' },
  withdrawn: { status: 'withdrawn', label: 'Withdrawn' }
};

/** Accepting or declining, with a note for the buyer that is up to the seller. */
const DecisionModal = ({ offer, decision, onSubmit, onClose }) => {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const accepting = decision === 'accepted';

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await onSubmit(note.trim() || null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={accepting ? `Accept ${formatXaf(offer.amount)} from ${offer.buyer?.name}?` : `Decline ${offer.buyer?.name}'s offer?`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="decision-form" className={`btn ${accepting ? 'btn-primary' : 'btn-danger'}`} disabled={busy}>
            {busy ? 'Saving...' : accepting ? 'Accept the offer' : 'Decline the offer'}
          </button>
        </>
      }
    >
      <p className="modal-lead">
        {accepting
          ? 'The listing shows as under offer, so other buyers know. Their offers stay open in case this one falls through. Mark it sold on your listings page once the deed is signed.'
          : 'The buyer is told it was not accepted.'}
      </p>
      <form id="decision-form" onSubmit={submit}>
        <Field
          label="A note for the buyer"
          hint={accepting ? 'For example: call me to arrange the viewing and bring your notary.' : 'Optional - what you would accept, perhaps.'}
        >
          <textarea rows="3" value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>
      </form>
    </Modal>
  );
};

/** The deed is signed and the money is in: the sale is done. */
const CompleteModal = ({ offer, onDone, onClose }) => {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const complete = async () => {
    setBusy(true);
    try {
      await api.post(`/landlord/offers/${offer.id}/complete`);
      toast.success('Marked sold. The buyer is asked to confirm, and then the land register names them the owner.');
      onDone();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title={`Sold to ${offer.buyer?.name} for ${formatXaf(offer.amount)}?`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Not yet
          </button>
          <button type="button" className="btn btn-primary" onClick={complete} disabled={busy}>
            {busy ? 'Saving...' : 'Yes, the sale is complete'}
          </button>
        </>
      }
    >
      <p className="modal-lead">
        Only once the deed is signed before the notary and you have been paid. The listing is marked sold, the other buyers
        are told, and the buyer confirms they have the land - after which the land register shows them as its owner and you
        can no longer sell it.
      </p>
    </Modal>
  );
};

/**
 * Every offer on the seller's listings, newest decisions waiting at the top.
 * The figures are what buyers say they will pay; the money and the deed are
 * settled between the two of them before a notary.
 */
const Offers = () => {
  useTitle('Offers');
  const toast = useToast();
  const { financeLabel } = useMeta();
  const offers = useFetch('/landlord/offers');
  const [deciding, setDeciding] = useState(null);
  const [completing, setCompleting] = useState(null);

  if (offers.loading && !offers.data) return <Spinner />;
  if (offers.error) {
    return (
      <div className="container section">
        <ErrorState message={offers.error} onRetry={offers.reload} />
      </div>
    );
  }

  const items = offers.data.items;
  const waiting = items.filter((offer) => offer.status === 'submitted').length;

  const decide = async (note) => {
    await api.patch(`/landlord/offers/${deciding.offer.id}`, { status: deciding.decision, note });
    toast.success(deciding.decision === 'accepted' ? 'Accepted. The listing now shows as under offer.' : 'Declined. The buyer has been told.');
    setDeciding(null);
    offers.reload();
  };

  const columns = [
    {
      key: 'listing',
      header: 'For',
      render: (offer) => (
        <div className="cell-stack">
          <Link to={`/properties/${offer.propertyId}`} className="cell-title">
            {offer.property?.title}
          </Link>
          <span className="muted">Asking {formatXaf(offer.property?.price)}</span>
        </div>
      )
    },
    {
      key: 'buyer',
      header: 'Buyer',
      render: (offer) => (
        <div className="cell-stack">
          <strong>{offer.buyer?.name}</strong>
          {offer.buyer?.phone ? <a href={`tel:${offer.buyer.phone}`}>{offer.buyer.phone}</a> : null}
          <span className="muted">{offer.buyer?.email}</span>
        </div>
      )
    },
    {
      key: 'offer',
      header: 'Offer',
      render: (offer) => {
        const gap = offer.property?.price ? offer.amount - offer.property.price : 0;
        return (
          <div className="cell-stack">
            <strong>{formatXaf(offer.amount)}</strong>
            {gap ? (
              <span className={gap < 0 ? 'muted' : 'facts-good'}>
                {formatXaf(Math.abs(gap))} {gap < 0 ? 'below' : 'above'} asking
              </span>
            ) : (
              <span className="muted">The asking price</span>
            )}
            <span className="muted">{financeLabel(offer.finance)}</span>
            {offer.message ? <span className="muted">"{offer.message}"</span> : null}
            <span className="muted">{timeAgo(offer.createdAt)}</span>
          </div>
        );
      }
    },
    {
      key: 'status',
      header: 'Status',
      render: (offer) => (
        <div className="cell-stack">
          <StatusBadge {...OFFER_BADGE[offer.status]} />
          {offer.decisionNote ? <span className="muted">"{offer.decisionNote}"</span> : null}
        </div>
      )
    },
    {
      key: 'actions',
      header: '',
      render: (offer) => (
        <div className="cell-actions">
          {offer.status === 'submitted' && offer.property?.status !== 'rented' ? (
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setDeciding({ offer, decision: 'accepted' })}>
              Accept
            </button>
          ) : null}
          {offer.status === 'accepted' ? (
            <Link to={`/offers/${offer.id}/agreement`} className="btn btn-outline btn-sm">
              Sale agreement
            </Link>
          ) : null}
          {offer.status === 'accepted' && offer.property?.status !== 'rented' ? (
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setCompleting(offer)}>
              Mark the sale complete
            </button>
          ) : null}
          {['submitted', 'accepted'].includes(offer.status) && offer.property?.status !== 'rented' ? (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDeciding({ offer, decision: 'declined' })}>
              Decline
            </button>
          ) : null}
          <Link to="/messages" className="btn btn-ghost btn-sm">
            Messages
          </Link>
        </div>
      )
    }
  ];

  return (
    <div className="container section">
      <header className="page-head">
        <div>
          <h1>Offers</h1>
          <p className="muted">
            What buyers have offered for the houses and land you are selling.
            {waiting ? ` ${waiting} waiting on you.` : ''}
          </p>
        </div>
        <Link to="/landlord/properties/new" className="btn btn-primary">
          Sell a house or land
        </Link>
      </header>

      <div className="notice notice-info">
        Nothing is paid through the site. Once you accept an offer, print its sale agreement for you and the buyer to sign
        before two witnesses, show them the land certificate and complete the sale before a notary - then press "Mark the
        sale complete" on that offer, which hands the plot to the buyer on the land register.
      </div>

      <DataTable
        columns={columns}
        rows={items}
        empty={{
          title: 'No offers yet',
          message: 'When you put a house or land up for sale, the offers buyers make land here.'
        }}
      />

      {completing ? (
        <CompleteModal
          offer={completing}
          onClose={() => setCompleting(null)}
          onDone={() => {
            setCompleting(null);
            offers.reload();
          }}
        />
      ) : null}

      {deciding ? (
        <DecisionModal offer={deciding.offer} decision={deciding.decision} onSubmit={decide} onClose={() => setDeciding(null)} />
      ) : null}
    </div>
  );
};

export default Offers;
